import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAdminApi } from '@/hooks/useAdminApi';
import { usePermissions } from '@/hooks/usePermissions';
import { useSubmissionLock } from '@/hooks/useSubmissionLock';
import {
  AlertCircle,
  Building2,
  Calendar,
  ChevronDown,
  ChevronRight,
  Edit,
  Eye,
  Globe2,
  Loader2,
  Plus,
  Search,
  Trash2,
  Trophy,
  UserMinus,
  Users,
  XCircle,
} from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

type EventStatus = 'upcoming' | 'active' | 'completed' | 'cancelled';
type EventVisibility = 'internal' | 'public';

type Event = {
  id: string;
  name: string;
  description: string | null;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  event_timezone: string | null;
  start_at_utc: string | null;
  end_at_utc: string | null;
  format: string | null;
  workout_id: string | null;
  max_participants: number | null;
  status: EventStatus;
  visibility: EventVisibility;
  affiliate_id: string | null;
  affiliate_name: string | null;
  workout_title: string | null;
  participant_count: number;
  spots_remaining: number | null;
  is_registered: boolean;
};

type Affiliate = { id: string; name: string; timezone: string };
type Workout = {
  id: string;
  name: string;
  scheduled_date: string | null;
  affiliate_id: string | null;
  source: string | null;
  visibility: string | null;
  shared_to_global: number;
};
type Participant = {
  id: string;
  user_id: string;
  user_name: string;
  user_email: string;
  signed_up_at: string;
};

type EventFormData = {
  name: string;
  description: string;
  event_date: string;
  start_time: string;
  end_time: string;
  event_timezone: string;
  format: string;
  workout_id: string;
  max_participants: string;
  affiliate_id: string;
  status: EventStatus;
  visibility: EventVisibility;
};

type EventPayload = {
  name: string;
  description?: string | null;
  event_date: string;
  start_time?: string | null;
  end_time?: string | null;
  event_timezone?: string | null;
  format?: string | null;
  workout_id?: string | null;
  max_participants?: number | null;
  affiliate_id?: string;
  status?: EventStatus;
  visibility?: EventVisibility;
};
type CreateEventPayload = {
  name: string;
  description?: string;
  event_date: string;
  start_time?: string;
  end_time?: string;
  event_timezone?: string;
  format?: string;
  workout_id?: string;
  max_participants?: number;
  affiliate_id?: string;
  visibility?: EventVisibility;
};

const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

function todayLocalDate(): string {
  const now = new Date();
  return [now.getFullYear(), now.getMonth() + 1, now.getDate()]
    .map((part, index) => (index === 0 ? String(part) : String(part).padStart(2, '0')))
    .join('-');
}

function todayInTimezone(timezone: string): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone || DEFAULT_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const values = Object.fromEntries(
      parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value])
    );
    if (values.year && values.month && values.day) {
      return `${values.year}-${values.month}-${values.day}`;
    }
  } catch {
    // The API will report invalid timezones; keep the form usable for editing.
  }
  return todayLocalDate();
}
const TIMEZONE_OPTIONS = Array.from(
  new Set([
    DEFAULT_TIMEZONE,
    'UTC',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Australia/Sydney',
    'Asia/Tokyo',
  ])
);

function isWorkoutSelectable(workout: Workout, affiliateId: string | null): boolean {
  const isGlobalWorkout =
    workout.source === 'hq' ||
    (workout.source === null && workout.affiliate_id === null) ||
    workout.visibility === 'global' ||
    workout.shared_to_global === 1;
  return isGlobalWorkout || Boolean(affiliateId && workout.affiliate_id === affiliateId);
}

const emptyForm: EventFormData = {
  name: '',
  description: '',
  event_date: '',
  start_time: '',
  end_time: '',
  event_timezone: DEFAULT_TIMEZONE,
  format: 'custom',
  workout_id: '',
  max_participants: '',
  affiliate_id: '',
  status: 'upcoming',
  visibility: 'internal',
};

const statusOptions: Array<{ value: EventStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const statusColors: Record<EventStatus, string> = {
  upcoming: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  active: 'bg-green-500/10 text-green-500 border-green-500/20',
  completed: 'bg-muted text-muted-foreground border-muted',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
};

const visibilityColors: Record<EventVisibility, string> = {
  internal: 'bg-muted text-muted-foreground border-muted',
  public: 'bg-primary/10 text-primary border-primary/20',
};

function eventDateLabel(value: string): string {
  const datePart = value.slice(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);
  if (!year || !month || !day) return value;
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function eventScheduleLabel(
  event: Pick<Event, 'event_date' | 'start_time' | 'end_time' | 'event_timezone'>
) {
  const date = eventDateLabel(event.event_date);
  if (!event.start_time || !event.end_time) return date;
  return `${date} · ${event.start_time}–${event.end_time} ${event.event_timezone || 'UTC'}`;
}

function eventToForm(event: Event): EventFormData {
  return {
    name: event.name,
    description: event.description || '',
    event_date: event.event_date.slice(0, 10),
    start_time: event.start_time || '',
    end_time: event.end_time || '',
    event_timezone: event.event_timezone || DEFAULT_TIMEZONE,
    format: event.format || 'custom',
    workout_id: event.workout_id || '',
    max_participants: event.max_participants?.toString() || '',
    affiliate_id: event.affiliate_id || '',
    status: event.status,
    visibility: event.visibility || 'internal',
  };
}

function formToPayload(
  form: EventFormData,
  includeAffiliate: boolean,
  allowPastDate = false
): EventPayload | null {
  const hasStart = Boolean(form.start_time);
  const hasEnd = Boolean(form.end_time);
  if (hasStart !== hasEnd) {
    toast.error('Enter both a start time and an end time, or leave both blank');
    return null;
  }
  if (!form.name.trim() || !form.event_date) {
    toast.error('Name and date are required');
    return null;
  }
  if (
    !allowPastDate &&
    form.event_date < todayInTimezone(form.event_timezone || DEFAULT_TIMEZONE)
  ) {
    toast.error('Event date cannot be in the past');
    return null;
  }

  const maxParticipants = form.max_participants.trim() ? Number(form.max_participants) : undefined;
  if (
    maxParticipants !== undefined &&
    (!Number.isInteger(maxParticipants) || maxParticipants < 1)
  ) {
    toast.error('Capacity must be a whole number greater than zero');
    return null;
  }

  const payload: EventPayload = {
    name: form.name.trim(),
    description: form.description.trim() || (includeAffiliate ? undefined : null),
    event_date: form.event_date,
    start_time: hasStart ? form.start_time : null,
    end_time: hasEnd ? form.end_time : null,
    event_timezone: hasStart ? form.event_timezone || DEFAULT_TIMEZONE : null,
    format: form.format || 'custom',
    workout_id: form.workout_id || null,
    max_participants: maxParticipants ?? (includeAffiliate ? undefined : null),
    status: form.status,
    visibility: form.visibility,
  };
  if (includeAffiliate && form.affiliate_id) payload.affiliate_id = form.affiliate_id;
  return payload;
}

function formToCreatePayload(form: EventFormData): CreateEventPayload | null {
  const payload = formToPayload(form, true);
  if (!payload) return null;
  return {
    name: payload.name,
    description: payload.description || undefined,
    event_date: payload.event_date,
    start_time: payload.start_time || undefined,
    end_time: payload.end_time || undefined,
    event_timezone: payload.event_timezone || undefined,
    format: payload.format || undefined,
    workout_id: payload.workout_id || undefined,
    max_participants: payload.max_participants ?? undefined,
    ...(payload.affiliate_id ? { affiliate_id: payload.affiliate_id } : {}),
    visibility: payload.visibility,
  };
}

export default function AdminEvents() {
  const { hasPermission } = usePermissions();
  const canManageEvents = hasPermission('create_events');
  const adminApi = useAdminApi();

  const [events, setEvents] = useState<Event[]>([]);
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<EventStatus | 'all'>('all');
  const [visibilityFilter, setVisibilityFilter] = useState<EventVisibility | 'all'>('all');
  const [affiliateFilter, setAffiliateFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const nextCursorRef = useRef<string | null>(null);
  const fetchGenerationRef = useRef(0);
  const [hasMore, setHasMore] = useState(false);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showRemoveParticipantDialog, setShowRemoveParticipantDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const removeParticipantRequestRef = useRef<{
    eventId: string;
    participantId: string;
    idempotencyKey: string;
  } | null>(null);
  const [formData, setFormData] = useState<EventFormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);
  const [participants, setParticipants] = useState<Record<string, Participant[]>>({});
  const [participantErrors, setParticipantErrors] = useState<Record<string, string>>({});
  const [loadingParticipants, setLoadingParticipants] = useState<Record<string, boolean>>({});
  const participantRequestIdsRef = useRef<Record<string, number>>({});
  const createSubmission = useSubmissionLock<CreateEventPayload>();
  const updateSubmission = useSubmissionLock<EventPayload>();

  const fetchEvents = useCallback(
    async (append = false) => {
      const requestGeneration = ++fetchGenerationRef.current;
      setIsLoading(true);
      setError(null);
      if (!append) {
        setHasMore(false);
        nextCursorRef.current = null;
      }
      try {
        const response = await adminApi.getEvents({
          status: statusFilter,
          visibility: visibilityFilter,
          affiliate_id: affiliateFilter === 'all' ? undefined : affiliateFilter,
          search: searchQuery.trim() || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          cursor: append ? nextCursorRef.current || undefined : undefined,
          limit: 20,
        });
        if (requestGeneration !== fetchGenerationRef.current) return;
        if (response.success && response.data) {
          const data = response.data;
          const incomingEvents: Event[] = data.events.map((event) => ({
            ...event,
            status: event.status as EventStatus,
            visibility: event.visibility as EventVisibility,
          }));
          setEvents((current) => (append ? [...current, ...incomingEvents] : incomingEvents));
          setHasMore(data.pagination.has_more);
          nextCursorRef.current = data.pagination.next_cursor;
        } else {
          setError(response.error || 'Failed to load events');
        }
      } catch (fetchError) {
        if (requestGeneration !== fetchGenerationRef.current) return;
        console.error('Failed to fetch events:', fetchError);
        setError('Failed to load events');
      } finally {
        if (requestGeneration === fetchGenerationRef.current) setIsLoading(false);
      }
    },
    [adminApi, affiliateFilter, dateFrom, dateTo, searchQuery, statusFilter, visibilityFilter]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void fetchEvents(false);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [fetchEvents]);

  useEffect(() => {
    void Promise.all([
      adminApi.getAffiliates().then((response) => {
        if (response.success && response.data) {
          setAffiliates(
            (response.data.affiliates || []).map(
              (affiliate: { id: string; name: string; timezone?: string | null }) => ({
                id: affiliate.id,
                name: affiliate.name,
                timezone: affiliate.timezone || 'UTC',
              })
            )
          );
        }
      }),
      adminApi.getEventWorkouts().then((response) => {
        if (response.success && response.data) setWorkouts(response.data.items || []);
      }),
    ]).catch(() => {
      // The event list remains usable if supporting selectors fail.
    });
  }, [adminApi]);

  const updateFilter = <T,>(setter: (value: T) => void, value: T) => {
    nextCursorRef.current = null;
    setter(value);
  };

  const fetchParticipants = async (event: Event, force = false) => {
    if (!force && expandedEventId === event.id) {
      setExpandedEventId(null);
      return;
    }
    setExpandedEventId(event.id);
    setParticipantErrors((current) => {
      const next = { ...current };
      delete next[event.id];
      return next;
    });
    const requestId = (participantRequestIdsRef.current[event.id] || 0) + 1;
    participantRequestIdsRef.current[event.id] = requestId;
    setLoadingParticipants((current) => ({ ...current, [event.id]: true }));
    try {
      const response = await adminApi.getEventParticipants(event.id);
      if (participantRequestIdsRef.current[event.id] !== requestId) return;
      if (response.success && response.data) {
        const data = response.data;
        setParticipants((current) => ({ ...current, [event.id]: data.participants }));
      } else {
        setParticipantErrors((current) => ({
          ...current,
          [event.id]: response.error || 'Failed to load participants',
        }));
      }
    } catch {
      if (participantRequestIdsRef.current[event.id] !== requestId) return;
      setParticipantErrors((current) => ({
        ...current,
        [event.id]: 'Failed to load participants',
      }));
    } finally {
      if (participantRequestIdsRef.current[event.id] === requestId) {
        setLoadingParticipants((current) => ({ ...current, [event.id]: false }));
      }
    }
  };

  const openCreateDialog = () => {
    setFormData({ ...emptyForm, event_timezone: DEFAULT_TIMEZONE });
    setShowCreateDialog(true);
  };

  const openEditDialog = (event: Event) => {
    setSelectedEvent(event);
    setFormData(eventToForm(event));
    setShowEditDialog(true);
  };

  const handleCreate = async () => {
    const payload = formToCreatePayload(formData);
    if (!payload) return;
    const attempt = createSubmission.begin(payload);
    if (!attempt) return;
    setIsSaving(true);
    try {
      const response = await adminApi.createEvent(attempt.payload, attempt.idempotencyKey);
      if (!response.success) {
        toast.error(response.error || 'Failed to create event');
        createSubmission.release();
        return;
      }
      createSubmission.reset();
      toast.success('Event created');
      setShowCreateDialog(false);
      setFormData(emptyForm);
      nextCursorRef.current = null;
      await fetchEvents(false);
    } catch {
      createSubmission.release();
      toast.error('Failed to create event');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedEvent) return;
    const payload = formToPayload(formData, false, true);
    if (!payload) return;
    const attempt = updateSubmission.begin(payload);
    if (!attempt) return;
    setIsSaving(true);
    try {
      const response = await adminApi.updateEvent(
        selectedEvent.id,
        attempt.payload,
        attempt.idempotencyKey
      );
      if (!response.success) {
        toast.error(response.error || 'Failed to update event');
        updateSubmission.release();
        return;
      }
      updateSubmission.reset();
      toast.success('Event updated');
      setShowEditDialog(false);
      setSelectedEvent(null);
      nextCursorRef.current = null;
      await fetchEvents(false);
    } catch {
      updateSubmission.release();
      toast.error('Failed to update event');
    } finally {
      setIsSaving(false);
    }
  };

  const updateStatus = async (event: Event, status: EventStatus) => {
    setIsSaving(true);
    try {
      const response = await adminApi.updateEvent(event.id, { status }, crypto.randomUUID());
      if (!response.success) {
        toast.error(response.error || `Failed to mark event ${status}`);
        return;
      }
      toast.success(status === 'cancelled' ? 'Event cancelled' : 'Event reopened');
      setShowCancelDialog(false);
      setSelectedEvent(null);
      nextCursorRef.current = null;
      await fetchEvents(false);
    } catch {
      toast.error(`Failed to mark event ${status}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent || selectedEvent.participant_count > 0) return;
    setIsSaving(true);
    try {
      const response = await adminApi.deleteEvent(selectedEvent.id);
      if (!response.success) {
        toast.error(response.error || 'Failed to delete event');
        return;
      }
      toast.success('Event deleted');
      setShowDeleteDialog(false);
      setSelectedEvent(null);
      nextCursorRef.current = null;
      await fetchEvents(false);
    } catch {
      toast.error('Failed to delete event');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveParticipant = async () => {
    if (!selectedEvent || !selectedParticipant) return;
    const request = removeParticipantRequestRef.current;
    if (
      !request ||
      request.eventId !== selectedEvent.id ||
      request.participantId !== selectedParticipant.id
    ) {
      return;
    }
    setIsSaving(true);
    try {
      const response = await adminApi.removeEventParticipant(
        selectedEvent.id,
        selectedParticipant.id,
        request.idempotencyKey
      );
      if (!response.success || !response.data) {
        toast.error(response.error || 'Failed to remove participant');
        return;
      }
      const data = response.data;
      setParticipants((current) => ({
        ...current,
        [selectedEvent.id]: (current[selectedEvent.id] || []).filter(
          (participant) => participant.id !== selectedParticipant.id
        ),
      }));
      setEvents((current) =>
        current.map((event) =>
          event.id === selectedEvent.id
            ? {
                ...event,
                participant_count: data.participant_count,
                spots_remaining:
                  event.max_participants === null
                    ? null
                    : Math.max(0, event.max_participants - data.participant_count),
              }
            : event
        )
      );
      toast.success('Participant removed; capacity is available again');
      setShowRemoveParticipantDialog(false);
      removeParticipantRequestRef.current = null;
      setSelectedParticipant(null);
      setSelectedEvent(null);
    } catch {
      toast.error('Failed to remove participant');
    } finally {
      setIsSaving(false);
    }
  };

  const stats = useMemo(
    () => ({
      total: events.length,
      upcoming: events.filter((event) => event.status === 'upcoming').length,
      active: events.filter((event) => event.status === 'active').length,
      participants: events.reduce((total, event) => total + event.participant_count, 0),
    }),
    [events]
  );

  const renderEventForm = (mode: 'create' | 'edit') => {
    const isEdit = mode === 'edit';
    const locked = isEdit ? updateSubmission.isLocked : createSubmission.isLocked;
    const selectableWorkouts = workouts.filter((workout) =>
      isWorkoutSelectable(workout, formData.affiliate_id)
    );
    return (
      <div className="grid gap-4 py-4">
        <div className="grid gap-2">
          <Label htmlFor={`${mode}-name`}>Event name *</Label>
          <Input
            id={`${mode}-name`}
            value={formData.name}
            onChange={(event) => setFormData({ ...formData, name: event.target.value })}
            placeholder="e.g. Summer Sprint Challenge"
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor={`${mode}-affiliate`}>Affiliate</Label>
          <select
            id={`${mode}-affiliate`}
            aria-label="Affiliate"
            disabled={isEdit}
            value={formData.affiliate_id}
            onChange={(event) => {
              const affiliate = affiliates.find((item) => item.id === event.target.value);
              setFormData({
                ...formData,
                affiliate_id: event.target.value,
                workout_id:
                  formData.workout_id &&
                  !workouts.some(
                    (workout) =>
                      workout.id === formData.workout_id &&
                      isWorkoutSelectable(workout, event.target.value)
                  )
                    ? ''
                    : formData.workout_id,
                ...(mode === 'create'
                  ? { event_timezone: affiliate?.timezone || DEFAULT_TIMEZONE }
                  : {}),
              });
            }}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
          >
            <option value="">No affiliate (global event)</option>
            {affiliates.map((affiliate) => (
              <option key={affiliate.id} value={affiliate.id}>
                {affiliate.name}
              </option>
            ))}
          </select>
          {isEdit && (
            <p className="text-xs text-muted-foreground">
              Affiliate cannot be changed after creation.
            </p>
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="grid gap-2">
            <Label htmlFor={`${mode}-date`}>Event date *</Label>
            <Input
              id={`${mode}-date`}
              type="date"
              min={todayInTimezone(formData.event_timezone || DEFAULT_TIMEZONE)}
              value={formData.event_date}
              onChange={(event) => setFormData({ ...formData, event_date: event.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`${mode}-start-time`}>Start time</Label>
            <Input
              id={`${mode}-start-time`}
              aria-label="Start time"
              type="time"
              value={formData.start_time}
              onChange={(event) => setFormData({ ...formData, start_time: event.target.value })}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`${mode}-end-time`}>End time</Label>
            <Input
              id={`${mode}-end-time`}
              aria-label="End time"
              type="time"
              value={formData.end_time}
              onChange={(event) => setFormData({ ...formData, end_time: event.target.value })}
            />
          </div>
        </div>
        <div className="grid gap-2">
          <Label htmlFor={`${mode}-timezone`}>Event timezone</Label>
          <Input
            id={`${mode}-timezone`}
            aria-label="Event timezone"
            list="event-timezones"
            value={formData.event_timezone}
            onChange={(event) => setFormData({ ...formData, event_timezone: event.target.value })}
            placeholder="Europe/London"
          />
          <datalist id="event-timezones">
            {TIMEZONE_OPTIONS.map((timezone) => (
              <option key={timezone} value={timezone} />
            ))}
          </datalist>
          <p className="text-xs text-muted-foreground">Only used when both times are provided.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor={`${mode}-format`}>Format</Label>
            <select
              id={`${mode}-format`}
              value={formData.format}
              onChange={(event) => setFormData({ ...formData, format: event.target.value })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="custom">Custom</option>
              <option value="sprint">Sprint</option>
              <option value="enduro">Enduro</option>
            </select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`${mode}-workout`}>Linked workout</Label>
            <select
              id={`${mode}-workout`}
              value={formData.workout_id}
              onChange={(event) => setFormData({ ...formData, workout_id: event.target.value })}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">No workout</option>
              {selectableWorkouts.map((workout) => (
                <option key={workout.id} value={workout.id}>
                  {workout.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="grid gap-2">
            <Label htmlFor={`${mode}-capacity`}>Maximum participants</Label>
            <Input
              id={`${mode}-capacity`}
              type="number"
              min="1"
              value={formData.max_participants}
              onChange={(event) =>
                setFormData({ ...formData, max_participants: event.target.value })
              }
              placeholder="Unlimited"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor={`${mode}-visibility`}>Visibility</Label>
            <select
              id={`${mode}-visibility`}
              aria-label="Visibility"
              value={formData.visibility}
              onChange={(event) =>
                setFormData({ ...formData, visibility: event.target.value as EventVisibility })
              }
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="internal">Internal</option>
              <option value="public">Public</option>
            </select>
          </div>
          {isEdit && (
            <div className="grid gap-2">
              <Label htmlFor="edit-status">Status</Label>
              <select
                id="edit-status"
                value={formData.status}
                onChange={(event) =>
                  setFormData({ ...formData, status: event.target.value as EventStatus })
                }
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {statusOptions
                  .filter((option) => option.value !== 'all')
                  .map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
              </select>
            </div>
          )}
        </div>
        <div className="grid gap-2">
          <Label htmlFor={`${mode}-description`}>Description</Label>
          <Textarea
            id={`${mode}-description`}
            value={formData.description}
            onChange={(event) => setFormData({ ...formData, description: event.target.value })}
            placeholder="Optional event description..."
            rows={3}
          />
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              if (isEdit) {
                updateSubmission.reset();
                setShowEditDialog(false);
              } else {
                createSubmission.reset();
                setShowCreateDialog(false);
              }
            }}
            disabled={locked || isSaving}
          >
            Cancel
          </Button>
          <Button onClick={isEdit ? handleEdit : handleCreate} disabled={locked || isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEdit ? 'Save changes' : 'Create event'}
          </Button>
        </DialogFooter>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">Events Management</h1>
          <p className="mt-1 text-muted-foreground">
            View and manage events across all affiliates.
          </p>
        </div>
        {canManageEvents && (
          <Button onClick={openCreateDialog} className="w-full sm:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Create Event
          </Button>
        )}
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: 'Loaded events', value: stats.total, icon: Trophy, color: 'text-primary' },
          {
            label: 'Loaded upcoming',
            value: stats.upcoming,
            icon: Calendar,
            color: 'text-blue-500',
          },
          { label: 'Loaded active', value: stats.active, icon: Globe2, color: 'text-green-500' },
          {
            label: 'Loaded participants',
            value: stats.participants,
            icon: Users,
            color: 'text-purple-500',
          },
        ].map(({ label, value, icon: Icon, color }) => (
          <Card key={label}>
            <CardContent className="p-5">
              <div className="flex items-center gap-4">
                <Icon className={`h-6 w-6 ${color}`} />
                <div>
                  <p className="text-sm text-muted-foreground">{label}</p>
                  <p className="text-2xl font-bold">{isLoading ? '…' : value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Search and narrow the event catalogue.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <div className="relative xl:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label="Search events"
              placeholder="Search events or affiliates..."
              className="pl-10"
              value={searchQuery}
              onChange={(event) => updateFilter(setSearchQuery, event.target.value)}
            />
          </div>
          <select
            aria-label="Status filter"
            value={statusFilter}
            onChange={(event) =>
              updateFilter(setStatusFilter, event.target.value as EventStatus | 'all')
            }
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            aria-label="Visibility filter"
            value={visibilityFilter}
            onChange={(event) =>
              updateFilter(setVisibilityFilter, event.target.value as EventVisibility | 'all')
            }
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">All visibility</option>
            <option value="internal">Internal</option>
            <option value="public">Public</option>
          </select>
          <select
            aria-label="Affiliate filter"
            value={affiliateFilter}
            onChange={(event) => updateFilter(setAffiliateFilter, event.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="all">All affiliates</option>
            {affiliates.map((affiliate) => (
              <option key={affiliate.id} value={affiliate.id}>
                {affiliate.name}
              </option>
            ))}
          </select>
          <div className="grid grid-cols-2 gap-2 xl:col-span-2">
            <Input
              aria-label="Date from filter"
              type="date"
              value={dateFrom}
              onChange={(event) => updateFilter(setDateFrom, event.target.value)}
            />
            <Input
              aria-label="Date to filter"
              type="date"
              value={dateTo}
              onChange={(event) => updateFilter(setDateTo, event.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {error && (
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-2 py-4 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
            <Button variant="ghost" className="ml-auto" onClick={() => void fetchEvents(false)}>
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            All events
            <Badge variant="secondary">{events.length}</Badge>
          </CardTitle>
          <CardDescription>Events across all affiliates and global events.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading && events.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : events.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              No events match these filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="px-3 py-3 font-medium">Event</th>
                    <th className="px-3 py-3 font-medium">Affiliate</th>
                    <th className="px-3 py-3 font-medium">Schedule</th>
                    <th className="px-3 py-3 font-medium">Participants</th>
                    <th className="px-3 py-3 font-medium">Status</th>
                    <th className="px-3 py-3 font-medium">Visibility</th>
                    <th className="px-3 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => {
                    const eventParticipants = participants[event.id] || [];
                    const expanded = expandedEventId === event.id;
                    return (
                      <Fragment key={event.id}>
                        <tr className="border-b align-top hover:bg-muted/30">
                          <td className="px-3 py-4">
                            <div className="flex items-start gap-2">
                              <Trophy className="mt-0.5 h-4 w-4 text-primary" />
                              <div>
                                <div className="font-medium">{event.name}</div>
                                {event.workout_title && (
                                  <div className="text-xs text-muted-foreground">
                                    {event.workout_title}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground" />
                              {event.affiliate_name || 'Global event'}
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap">
                            {eventScheduleLabel(event)}
                          </td>
                          <td className="px-3 py-4">
                            {event.participant_count}
                            {event.max_participants !== null && ` / ${event.max_participants}`}
                          </td>
                          <td className="px-3 py-4">
                            <Badge variant="outline" className={statusColors[event.status]}>
                              {event.status}
                            </Badge>
                          </td>
                          <td className="px-3 py-4">
                            <Badge variant="outline" className={visibilityColors[event.visibility]}>
                              {event.visibility === 'public' ? 'Public' : 'Internal'}
                            </Badge>
                          </td>
                          <td className="px-3 py-4">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`${expanded ? 'Hide' : 'View'} participants for ${event.name}`}
                                onClick={() => void fetchParticipants(event)}
                                title="View participants"
                              >
                                {expanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                              {canManageEvents && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Edit ${event.name}`}
                                    onClick={() => openEditDialog(event)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  {event.status === 'cancelled' ? (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label={`Reopen ${event.name}`}
                                      onClick={() => void updateStatus(event, 'upcoming')}
                                    >
                                      <Eye className="h-4 w-4 text-green-500" />
                                    </Button>
                                  ) : event.status !== 'completed' ? (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      aria-label={`Cancel ${event.name}`}
                                      onClick={() => {
                                        setSelectedEvent(event);
                                        setShowCancelDialog(true);
                                      }}
                                    >
                                      <XCircle className="h-4 w-4 text-amber-500" />
                                    </Button>
                                  ) : null}
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    aria-label={`Delete ${event.name}`}
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => {
                                      setSelectedEvent(event);
                                      setShowDeleteDialog(true);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                        {expanded && (
                          <tr key={`${event.id}-participants`} className="border-b bg-muted/20">
                            <td colSpan={7} className="px-3 py-4">
                              {participantErrors[event.id] ? (
                                <div className="flex flex-wrap items-center gap-3 text-sm text-destructive">
                                  <span>{participantErrors[event.id]}</span>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => void fetchParticipants(event, true)}
                                  >
                                    Retry
                                  </Button>
                                </div>
                              ) : loadingParticipants[event.id] ? (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <Loader2 className="h-4 w-4 animate-spin" /> Loading participants…
                                </div>
                              ) : eventParticipants.length === 0 ? (
                                <p className="text-sm text-muted-foreground">
                                  No participants registered.
                                </p>
                              ) : (
                                <div className="rounded-md border">
                                  <div className="grid grid-cols-[1.2fr_1.5fr_1fr_auto] gap-3 border-b px-3 py-2 text-xs font-medium uppercase text-muted-foreground">
                                    <span>Name</span>
                                    <span>Email</span>
                                    <span>Signed up</span>
                                    <span />
                                  </div>
                                  {eventParticipants.map((participant) => (
                                    <div
                                      key={participant.id}
                                      className="grid grid-cols-[1.2fr_1.5fr_1fr_auto] items-center gap-3 px-3 py-3 text-sm"
                                    >
                                      <span>{participant.user_name}</span>
                                      <span className="break-all">{participant.user_email}</span>
                                      <span>
                                        {new Date(participant.signed_up_at).toLocaleString()}
                                      </span>
                                      {canManageEvents && (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          aria-label={`Remove ${participant.user_name}`}
                                          className="text-destructive hover:text-destructive"
                                          onClick={() => {
                                            setSelectedEvent(event);
                                            setSelectedParticipant(participant);
                                            removeParticipantRequestRef.current = {
                                              eventId: event.id,
                                              participantId: participant.id,
                                              idempotencyKey: crypto.randomUUID(),
                                            };
                                            setShowRemoveParticipantDialog(true);
                                          }}
                                        >
                                          <UserMinus className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {hasMore && (
            <div className="mt-4 flex justify-center">
              <Button variant="outline" onClick={() => void fetchEvents(true)} disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Load more events
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={showCreateDialog}
        onOpenChange={(open) => {
          if (!open && (createSubmission.isLocked || isSaving)) return;
          setShowCreateDialog(open);
          if (!open) createSubmission.reset();
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[650px]">
          <DialogHeader>
            <DialogTitle>Create event</DialogTitle>
            <DialogDescription>
              Create an event for an affiliate or the wider community. Date-only events are
              supported.
            </DialogDescription>
          </DialogHeader>
          {renderEventForm('create')}
        </DialogContent>
      </Dialog>

      <Dialog
        open={showEditDialog}
        onOpenChange={(open) => {
          if (!open && (updateSubmission.isLocked || isSaving)) return;
          setShowEditDialog(open);
          if (!open) updateSubmission.reset();
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[650px]">
          <DialogHeader>
            <DialogTitle>Edit event</DialogTitle>
            <DialogDescription>Update every business field except the affiliate.</DialogDescription>
          </DialogHeader>
          {renderEventForm('edit')}
        </DialogContent>
      </Dialog>

      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Cancel event?</DialogTitle>
            <DialogDescription>
              “{selectedEvent?.name}” will remain in the system with its participants, but new
              registrations will be blocked.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(false)}
              disabled={isSaving}
            >
              Keep event
            </Button>
            <Button
              variant="destructive"
              onClick={() => selectedEvent && void updateStatus(selectedEvent, 'cancelled')}
              disabled={isSaving}
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Cancel event
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Delete event?</DialogTitle>
            <DialogDescription>
              {selectedEvent && selectedEvent.participant_count > 0
                ? 'This event has participants and cannot be deleted. Cancel it instead to preserve its history.'
                : `Delete “${selectedEvent?.name}”? This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={isSaving}
            >
              Close
            </Button>
            {selectedEvent && selectedEvent.participant_count === 0 && (
              <Button variant="destructive" onClick={() => void handleDelete()} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Delete event
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showRemoveParticipantDialog}
        onOpenChange={(open) => {
          setShowRemoveParticipantDialog(open);
          if (!open) removeParticipantRequestRef.current = null;
        }}
      >
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Remove participant?</DialogTitle>
            <DialogDescription>
              Remove {selectedParticipant?.user_name} from “{selectedEvent?.name}”? Capacity will
              become available for registration again.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                removeParticipantRequestRef.current = null;
                setShowRemoveParticipantDialog(false);
              }}
              disabled={isSaving}
            >
              Keep participant
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleRemoveParticipant()}
              disabled={isSaving}
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Remove participant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
